import type { Crop } from 'react-image-crop';
import type { GridChildComponentProps } from '@shared/components/VirtualScroll';
import type { WebsitePhotoAlignment } from '@shared/types/WebsiteHeaderSettings.types';
import type { MediaPhoto as MediaPhotoQueryResult } from '@apps/admin/components/PhotoUploader/PhotoUploader.types';
import type { PhotoUploadUseCase } from '@shared/utils/photoUpload';

export enum PICKER_TYPES {
  ADD_TO_COLLECTION = 'ADD_TO_COLLECTION',
  PICK_FROM_ALL_PHOTOS = 'PICK_FROM_ALL_PHOTOS',
}

export type PhotoCropData = Readonly<{
  photoId: string;
  aspectRatio: number | 'locked' | null;
  crop?: Crop;
  alignment?: WebsitePhotoAlignment;
  mobileAspectRatio?: number;
  mobileCrop?: Crop;
  url: string;
  height: number;
  width: number;
}>;

export type SecondaryFooterAction = Readonly<{ label: string; onClick: () => void }>;
// Factory form: called with the live in-picker selection count so the dialog can
// compute visibility and label without an external state roundtrip.
export type SecondaryFooterActionFactory = (selectedCount: number) => SecondaryFooterAction | undefined;

export type AddMediaArgs = Readonly<{
  selectedItemsId: ReadonlyArray<string>;
  cropData: Crop | null;
  alignmentData: WebsitePhotoAlignment | null;
  mobileCropData: Crop | null;
  cb: () => void;
}>;

/**
 * Video and crop never share a picker: a crop host sets one image and never draws video, and a video host
 * never crops. The union lets the type checker reject a host that asks for both.
 */
export type PhotoCropOrVideoProps =
  | Readonly<{
      supportsPhotoCrop: true;
      canShowVideo?: false;
    }>
  | Readonly<{
      supportsPhotoCrop?: false;
      /**
       * The host's own answer to "can I draw a video tile, and can I act on a video id handed back?".
       * Default **false**: most of the ~18 surfaces sharing this dialog hand their single pick to an `<img>`.
       * `useCase` cannot stand in for it — `websitePhotos` is passed by the album picker and by eight
       * photo-only hosts alike. No rollout flag is involved: turning `albumVideoUploadsEnabled` off stops new
       * video being uploaded, and must never retire a video a couple already has.
       */
      canShowVideo?: boolean;
    }>;

export type PhotoPickerDialogProps = Readonly<{
  isOpen: boolean;
  /**
   * Stacking layer for the dialog. Defaults to JoyKit's `modal` layer; a caller that opens this
   * picker from a surface which itself sits above that layer (the Go Live Checks wizard's mobile
   * drawer, on `popover`) has to raise it, or the picker opens underneath its own opener.
   */
  zIndex?: number | string;
  /**
   * Move focus into the dialog, trap it, and return it on close — for an opener whose focus lives in an
   * iframe (the WebsiteV2 editor). Defaults to `false`, which keeps the focus lock off for every other
   * surface. Only takes effect on the upload-widget path: the legacy Filestack picker mounts outside the
   * dialog, and the trap would pull keyboard focus back out of it.
   */
  trapFocus?: boolean;
  /** Element to focus when the dialog closes. Only used while `trapFocus` is on. */
  finalFocusRef?: React.RefObject<{ focus(): void }>;
  handleClose: () => void;
  handleOpenFilePicker: () => void;
  isFetching: boolean;
  media?: ReadonlyArray<MediaPhotoQueryResult> | null;
  handleAddMedia: (args: AddMediaArgs) => void;
  isAddingMedia: boolean;
  collectionId?: string;
  showCount?: boolean;
  pickerType?: PICKER_TYPES;
  maxCount?: number;
  /**
   * Upload cap for the in-dialog upload widget. Defaults to `maxCount`, so a single-select picker also
   * uploads one photo at a time. Raise it to let a single-select surface upload a batch into the library
   * and still select one (the newest upload is auto-selected within `maxCount`).
   */
  maxUploadCount?: number;
  minCount?: number;
  photoSelectionHelperText?: string;
  libraryTitle?: string;
  selectedItems?: ReadonlyArray<string>;
  disabledItems?: ReadonlyArray<string>;
  uploadInProgressCount?: number;
  photoCropData?: PhotoCropData;
  /**
   * The calling surface. Decides whether this picker may open the upload widget: only allowlisted
   * subAreas do. Omitting it means the surface keeps the legacy Filestack path.
   */
  useCase?: PhotoUploadUseCase;
  /**
   * Hides "Upload from phone": the library tile and the in-dialog upload widget's Phone source. Phone uploads
   * always save to the event's photo library, so surfaces that store images elsewhere (custom art) opt out.
   * The unified picker doesn't read it.
   */
  hidePhoneUpload?: boolean;
  defaultTab?: TabType;
  enableMobileCrop?: boolean;
  customHeaderContent?: React.ReactNode;
  primaryCTALabel?: string;
  dialogTitle?: string;
  disableAutoCloseOnFilePicker?: boolean;
  onSelectionChange?: (selectedItemsId: ReadonlyArray<string>) => void;
  showBulkActionCTAs?: boolean;
  allowEmptySelection?: boolean;
  /**
   * Auto-select media that arrives after the picker opens (i.e. just-uploaded photos).
   *
   * Defaults to ON for every surface, including capped and crop pickers. A capped picker stays within its
   * cap: the newest uploads win and older picks drop off the front, the same rule that applies when you tap
   * a photo while already at the cap. A crop picker moves its crop target to the new photo. Pass `false` to
   * opt a surface out.
   */
  autoSelectNewUploads?: boolean;
  secondaryFooterAction?: SecondaryFooterAction | SecondaryFooterActionFactory;
}> &
  PhotoCropOrVideoProps;

export type PhotoPickerLibraryProps = Readonly<{
  isFetching: boolean;
  media?: ReadonlyArray<MediaPhotoQueryResult> | null;
  selectedItemsId: ReadonlyArray<string>;
  disabledItems: ReadonlyArray<string>;
  updateSelectedItemsId: (newSelectedItemId: string) => void;
  handleOpenFilePicker: () => void;
  showCount?: boolean;
  photoSelectionHelperText?: string;
  libraryTitle?: string;
  uploadInProgressCount?: number;
  /**
   * Hide the in-grid "Add photos" / "Upload from phone" tiles. The unified picker sets this because those
   * entry points are handled by the source sidebar instead — leaving them would be a duplicate control.
   */
  hideUploadTiles?: boolean;
  /** Hides only the "Upload from phone" tile; see `PhotoPickerDialogProps.hidePhoneUpload`. */
  hidePhoneUpload?: boolean;
  /**
   * Whether this surface resolved to the upload widget. Passed down rather than re-read from the flag:
   * the flag alone isn't the answer (see `resolveIsWidgetEnabled`), and a child reading it directly would
   * show widget copy on a surface that's actually still on Filestack.
   */
  widgetEnabled?: boolean;
  /**
   * Unified picker: render the grid with square (1:1), cover-filled, 10px-radius tiles and a 12px row gap
   * (per design). Other pickers omit this and keep the fixed-height tiles.
   */
  squareTiles?: boolean;
  /** Draw videos in the grid. The dialog passes its host's `canShowVideo`; see that prop. */
  showVideo?: boolean;
}>;

export type DialogFooterProps = Readonly<{
  handleSelectAll?: () => void;
  handleDeselect?: () => void;
  handleCancel: () => void;
  handleAdd: () => void;
  isAdding: boolean;
  disableAddCTA: boolean;
  disableSelectAllCTA?: boolean;
  disableDeselectAllCTA?: boolean;
  showBulkActionCTAs?: boolean;
  pickerType?: PICKER_TYPES;
  selectedItemsCount?: number;
  supportsPhotoCrop?: boolean;
  /** Whether the host opted into video. Drives media vs photo wording on the selection CTA. */
  canShowVideo?: boolean;
  selectedTab?: TabType;
  enableMobileCrop?: boolean;
  primaryCTALabel?: string;
  secondaryFooterAction?: SecondaryFooterAction;
  /** Hide the footer Cancel button (the unified picker relies on the dialog's top-right X instead). */
  hideCancel?: boolean;
  /**
   * Hide the primary confirm CTA entirely. Used by the page-level upload surface, where there's no album to
   * add to — the upload already lands in the library, so an "Add to Album" button would be a no-op close.
   */
  hidePrimaryAction?: boolean;
}>;

export type MediaPhoto = Readonly<{
  id: string;
  photo: {
    id: string;
    url: string;
    height: number;
    width: number;
  };
}>;

/** A cell in the library grid, discriminated so the compiler keeps a video out of the photo branch. */
export type LibraryPhotoItem = Readonly<{
  kind: 'photo';
  id: string;
  photo: {
    id: string;
    url: string;
    height: number;
    width: number;
  };
}>;

export type LibraryVideoItem = Readonly<{
  kind: 'video';
  id: string;
  video: {
    id: string;
    url: string;
    /** The video's first frame, already rendition-sized by the caller. */
    thumbnailUrl?: string;
    mimeType: string;
  };
}>;

export type LibraryMediaItem = LibraryPhotoItem | LibraryVideoItem;

export type GridItemProps = GridChildComponentProps<{
  mediaArray: ReadonlyArray<LibraryMediaItem>;
  updateSelectedItemsId: (newSelectedItemId: string) => void;
  selectedItemsId: ReadonlyArray<string>;
  disabledItems: ReadonlyArray<string>;
  showCount: boolean;
  /** Unified picker: render square (1:1), cover-filled, 10px-radius tiles instead of the fixed-height default. */
  squareTiles: boolean;
}>;

export enum TabType {
  SELECT_PHOTO = 'selectPhoto',
  CROP = 'crop',
  MOBILE_CROP = 'mobileCrop',
}

export type PhotoDisplayConfig = Readonly<{
  crop: Crop | undefined;
  focusPoint: WebsitePhotoAlignment | undefined;
  mobileCrop: Crop | undefined;
}>;

export type DialogHeaderProps = Readonly<{
  handleClose: () => void;
  dialogTitle: string;
}>;

export type TabNavigationProps = Readonly<{
  selectedTab: TabType;
  onSelectTab: (tab: TabType) => void;
  isCropTabDisabled: boolean;
  enableMobileCrop?: boolean;
  isMobileCropTabDisabled?: boolean;
}>;

export interface PhotoCardSkeletonGroupProps
  extends Readonly<{
    loading: boolean;
    count?: number;
    height?: number;
  }> {}
